import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CardList from "../ui/CardList.jsx";
import ContactActions from "../ui/ContactActions.jsx";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import { addressLine } from "../ui/contactLinks";
import { phoneUi } from "../ui/phoneUi";
import { VENDOR_SORTS, filterAndSortVendors, loadVendors, saveVendor, vendorForm } from "../../features/vendors/vendorData";
import "../mobile.css";

// The phone Vendors page (plan Phase 2): search and sort; one vendor (?id=N) with call / email / map;
// edit its fields or add a vendor. Delete stays on the desktop. Layout only:
// features/vendors/vendorData.js is the desktop page's own code.

function vendorIdOf(searchParams) {
  const raw = searchParams.get("id");
  if (!raw || !/^\d+$/.test(raw)) return null;
  const n = Number(raw);
  return n > 0 ? n : null;
}

export default function VendorsScreen() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [listState, setListState] = useState({ searchTerm: "", sortBy: "name_asc" });
  const [vendors, setVendors] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // {vendor} (vendor null = a new one) | null
  const navigate = useNavigate();
  const fromList = useRef(false);
  const id = vendorIdOf(searchParams);

  const reread = useCallback(async () => {
    try {
      setVendors(await loadVendors());
      setError(null);
    } catch (err) {
      setError(err.message || "Couldn't reach JobQ.");
      setVendors((v) => v || []);
    }
  }, []);
  useEffect(() => { reread(); }, [reread]);

  const closePanel = useCallback(() => setEditing(null), []);
  const toList = () => {
    if (fromList.current) { fromList.current = false; navigate(-1); } else setSearchParams({}, { replace: true });
  };
  const vendor = id && vendors ? vendors.find((v) => v.vendor_id === id) : null;

  return (
    <div className="jq-screen" data-testid={id ? "phone-vendor" : "phone-vendors"}>
      {id ? (
        <VendorDetail vendor={vendor} loading={vendors === null} error={error} onAll={toList} onEdit={() => setEditing({ vendor })} />
      ) : (
        <VendorList
          vendors={vendors}
          error={error}
          state={listState}
          setState={setListState}
          onOpen={(vid) => { fromList.current = true; setSearchParams({ id: String(vid) }); }}
          onAdd={() => setEditing({ vendor: null })}
        />
      )}
      <VendorPanel
        open={!!editing}
        vendor={editing?.vendor || null}
        onClose={closePanel}
        onSaved={() => { setEditing(null); reread(); }}
      />
    </div>
  );
}

function VendorList({ vendors, error, state, setState, onOpen, onAdd }) {
  const { searchTerm, sortBy } = state;
  const shown = useMemo(() => filterAndSortVendors(vendors || [], { searchTerm, sortBy }), [vendors, searchTerm, sortBy]);
  return (
    <>
      <button type="button" className="btn btn-outline-primary w-100 mb-2" onClick={onAdd}>Add a vendor</button>
      <CardList
        items={shown}
        getKey={(v) => v.vendor_id}
        onItemClick={(v) => onOpen(v.vendor_id)}
        renderCard={(v) => (
          <div data-testid={`vendor-card-${v.vendor_id}`}>
            <div className="jq-qcard-top">
              <span className="jq-qcard-title mb-0">{v.name}</span>
              {v.is_service && <span className="jq-tag">Service</span>}
            </div>
            <div className="jq-qcard-meta">
              #{v.vendor_id}{v.contact_name ? ` · ${v.contact_name}` : ""}{v.city || v.state ? ` · ${[v.city, v.state].filter(Boolean).join(", ")}` : ""}
            </div>
            {v.is_service && v.service_types && <div className="jq-muted">{v.service_types}</div>}
          </div>
        )}
        loading={vendors === null}
        error={error}
        empty={(vendors || []).length ? `No vendors match "${searchTerm}".` : "No vendors yet."}
        testId="vendor-list"
        search={{ value: searchTerm, onChange: (v) => setState((p) => ({ ...p, searchTerm: v })), placeholder: "Name, contact or #" }}
        filters={{
          activeCount: sortBy !== "name_asc" ? 1 : 0,
          resultLabel: `${shown.length} vendor${shown.length === 1 ? "" : "s"}`,
          onClear: () => setState((p) => ({ ...p, sortBy: "name_asc" })),
          render: () => (
            <>
              <div className="form-label">Sort by</div>
              <div className="d-flex flex-wrap gap-2">
                {VENDOR_SORTS.map((o) => (
                  <button key={o.value} type="button" className={`btn btn-sm ${sortBy === o.value ? "btn-primary" : "btn-outline-primary"}`} aria-pressed={sortBy === o.value} onClick={() => setState((p) => ({ ...p, sortBy: o.value }))}>
                    {o.label}
                  </button>
                ))}
              </div>
            </>
          ),
        }}
      />
    </>
  );
}

function VendorDetail({ vendor, loading, error, onAll, onEdit }) {
  const back = <button type="button" className="btn btn-link px-0 mb-2" onClick={onAll}><i className="fas fa-chevron-left me-1" aria-hidden="true" />All vendors</button>;
  if (loading) return <div className="jq-list-state" role="status"><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Loading…</div>;
  if (!vendor) return <>{back}<div className="jq-note jq-note-danger" role="alert">{error || "That vendor isn't in JobQ."}</div></>;
  const line = addressLine(vendor);
  return (
    <>
      {back}
      <section className="jq-card" data-testid="vendor-header">
        <div className="d-flex align-items-center gap-2">
          <span className="fw-bold fs-5 flex-grow-1">{vendor.name}</span>
          {vendor.is_service && <span className="jq-tag">Service</span>}
        </div>
        <div className="jq-muted">Vendor #{vendor.vendor_id}</div>
        {vendor.contact_name && <div className="fw-semibold">{vendor.contact_name}</div>}
        {(vendor.phone || vendor.email) && <div className="jq-muted">{[vendor.phone, vendor.email].filter(Boolean).join(" · ")}</div>}
        {line && <div className="jq-muted">{line}</div>}
        <ContactActions phone={vendor.phone} email={vendor.email} address={line} testId="vendor-actions" />
        {vendor.is_service && vendor.service_types && <div className="mt-2">Services: {vendor.service_types}</div>}
        {vendor.notes && <div className="jq-muted mt-2" style={{ whiteSpace: "pre-wrap" }}>{vendor.notes}</div>}
      </section>
      <button type="button" className="btn btn-outline-primary w-100" onClick={onEdit}>Edit this vendor</button>
    </>
  );
}

function VendorPanel({ open, vendor, onClose, onSaved }) {
  const [form, setForm] = useState(vendorForm(null));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => { if (open) { setForm(vendorForm(vendor)); setError(null); } }, [open, vendor]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setError(null);
    const r = await saveVendor({ vendorId: vendor ? vendor.vendor_id : null, form, ui: phoneUi, setBusy });
    if (r.error) setError(r.error);
    if (r.done) onSaved();
  };
  const text = (k, label, props = {}) => (
    <div className="mb-3">
      <label className="form-label" htmlFor={`vd-${k}`}>{label}</label>
      <input id={`vd-${k}`} className="form-control" value={form[k]} onChange={set(k)} autoComplete="off" {...props} />
    </div>
  );
  return (
    <FullScreenPanel
      open={open}
      title={vendor ? `Edit ${vendor.name}` : "Add a vendor"}
      onClose={busy ? undefined : onClose}
      testId="vendor-panel"
      footer={<button type="button" className="btn btn-primary btn-lg w-100" disabled={busy} onClick={save}>{busy ? "Saving…" : vendor ? "Save changes" : "Add the vendor"}</button>}
    >
      {error && <div className="jq-note jq-note-danger mb-2" role="alert">{error}</div>}
      {text("name", "Name")}
      {text("contact_name", "Contact name")}
      {text("phone", "Phone", { type: "tel", inputMode: "tel" })}
      {text("email", "Email", { type: "email", inputMode: "email" })}
      {text("address", "Address")}
      {text("city", "City")}
      {text("state", "State")}
      {text("zip_code", "Zip code", { inputMode: "numeric" })}
      <div className="mb-3">
        <label className="form-label" htmlFor="vd-notes">Notes</label>
        <textarea id="vd-notes" className="form-control" rows={3} value={form.notes} onChange={set("notes")} />
      </div>
      <label className="form-check mb-2">
        <input className="form-check-input" type="checkbox" checked={form.is_service} onChange={(e) => setForm((f) => ({ ...f, is_service: e.target.checked }))} />
        <span className="form-check-label">Service vendor (coater / plater / finisher)</span>
      </label>
      {form.is_service && text("service_types", "Service types (optional)", { placeholder: "e.g. Powder Coat, Anodize, Zinc Plate" })}
    </FullScreenPanel>
  );
}
